import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 시나리오 챗봇 idle 타이머 훅
 *
 * 사용자가 일정 시간 액션(클릭/타이핑/스크롤)이 없으면 콜백 트리거.
 * 정본 디폴트: 2분 (XMind 30초 vs PPTX 5분 절충안, 회신 후 조정).
 *
 * 사용법:
 *   const { isIdle, reset } = useIdleTimer({
 *     timeoutMs: 2 * 60 * 1000,
 *     onIdle: () => showIdlePrompt(),
 *     enabled: !isInputNode && !isLookupLoading,
 *     watchTarget: panelRef,  // panel 내부 활동만 감지 (외부 사이트 무관)
 *   });
 *
 * 반환:
 *   isIdle: boolean — 현재 idle 상태
 *   reset:  () => void — 외부에서 강제 리셋
 */
export default function useIdleTimer({
  timeoutMs = 2 * 60 * 1000,
  onIdle,
  enabled = true,
  watchTarget = null,  // null 이면 window 전체
}) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef(null);
  const onIdleRef = useRef(onIdle);

  // onIdle 콜백 최신값 유지 (effect 재구독 방지)
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const reset = useCallback(() => {
    setIsIdle(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled) return;
    timerRef.current = setTimeout(() => {
      setIsIdle(true);
      onIdleRef.current?.();
    }, timeoutMs);
  }, [timeoutMs, enabled]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsIdle(false);
      return undefined;
    }

    const target = watchTarget?.current || window;
    const events = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    reset();
    events.forEach((ev) => target.addEventListener(ev, reset, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((ev) => target.removeEventListener(ev, reset));
    };
  }, [enabled, reset, watchTarget]);

  return { isIdle, reset };
}
